import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@/components/ui';
import type { AdminMemberDetail, MemberType } from '../hooks/useAdminUsersQuery';
import { memberTypeLabel } from '../lib/userLabels';
import { useMemberTypeMutation } from '../hooks/useMemberTypeMutation';
import { useMemberTypeDisplay } from '../hooks/useMemberTypeDisplay';

interface MemberTypeChangeControlProps {
  user: AdminMemberDetail;
}

/** 현재 유형의 반대 유형(전환 대상). INDIVIDUAL(legacy)은 PERSONAL 로 취급. */
const targetTypeOf = (current: MemberType): Exclude<MemberType, 'INDIVIDUAL'> =>
  current === 'BUSINESS' ? 'PERSONAL' : 'BUSINESS';

/**
 * B2B(2026-06-20) D3 — 회원 상세에서 개인 ↔ 기업 유형을 전환한다.
 *
 * - PERSONAL → BUSINESS: 모달에서 상호 + 사업자등록번호 필수 입력(즉시 ACTIVE 는 BE 처리).
 * - BUSINESS → PERSONAL: 확인만 받고 전환(상호/번호/등급은 BE 가 보존, 화면 미노출).
 *
 * 라벨만 노출(raw enum 미노출). 성공 시 토스트 + 상세/목록 무효화는 훅에서 처리.
 */
export const MemberTypeChangeControl = ({ user }: MemberTypeChangeControlProps) => {
  const { t } = useTranslation('common');
  const mutation = useMemberTypeMutation();
  const [open, setOpen] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessRegistrationNo, setBusinessRegistrationNo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const target = targetTypeOf(user.memberType);
  const toBusiness = target === 'BUSINESS';

  // 모달 열 때마다 초기화. BUSINESS→PERSONAL 역전환 후 다시 열릴 때 잔존 입력 방지.
  useEffect(() => {
    if (open) {
      setBusinessName(user.businessName ?? '');
      setBusinessRegistrationNo(user.businessRegistrationNo ?? '');
      setError(null);
    }
  }, [open, user.businessName, user.businessRegistrationNo]);

  // QA-414 — 사업자는 현재 등급까지(예: 사업자(1등급)).
  const currentLabel = useMemberTypeDisplay(user.memberType, user.gradeId);
  const targetLabel = useMemo(() => memberTypeLabel(t, target), [t, target]);

  const handleConfirm = () => {
    setError(null);
    if (toBusiness) {
      if (!businessName.trim() || !businessRegistrationNo.trim()) {
        setError(t('users.memberTypeChange.requiredError', '상호와 사업자등록번호를 입력해 주세요.'));
        return;
      }
    }
    mutation.mutate(
      {
        userId: user.userId,
        memberType: target,
        ...(toBusiness
          ? {
              businessName: businessName.trim(),
              businessRegistrationNo: businessRegistrationNo.trim(),
            }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            t('users.memberTypeChange.toast', {
              type: targetLabel,
              defaultValue: `{{type}} 회원으로 전환되었습니다`,
            }),
          );
          setOpen(false);
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <>
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl p-4"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
        data-testid="member-type-change-control"
      >
        <div className="space-y-0.5">
          <p className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
            {t('users.memberTypeChange.title', '회원 유형')}
          </p>
          <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
            {t('users.memberTypeChange.current', { type: currentLabel, defaultValue: '현재 유형: {{type}}' })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          {t('users.memberTypeChange.action', { type: targetLabel, defaultValue: '{{type}} 회원으로 변경' })}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o && !mutation.isPending) setOpen(false); }}>
        <DialogContent data-testid="member-type-change-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('users.memberTypeChange.modalTitle', { type: targetLabel, defaultValue: '{{type}} 회원으로 전환' })}
            </DialogTitle>
            <DialogDescription>
              {toBusiness
                ? t(
                    'users.memberTypeChange.toBusinessDesc',
                    '상호와 사업자등록번호를 입력하면 즉시 기업회원으로 전환되고 승인 상태가 됩니다. 회원 등급은 바뀌지 않으니, 필요하면 [등급 정보] 탭에서 등급을 지정해 주세요.',
                  )
                : t(
                    'users.memberTypeChange.toPersonalDesc',
                    '개인회원으로 전환합니다. 회원 등급과 등급 혜택(할인·적립)은 그대로 유지됩니다. 혜택을 없애려면 [등급 정보] 탭에서 등급을 "일반회원"으로 바꿔 주세요. 상호·사업자등록번호는 보존됩니다.',
                  )}
            </DialogDescription>
          </DialogHeader>

          {toBusiness ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="member-type-business-name" required>
                  {t('users.memberTypeChange.businessName', '상호')}
                </Label>
                <Input
                  id="member-type-business-name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  maxLength={200}
                  disabled={mutation.isPending}
                  placeholder={t('users.memberTypeChange.businessNamePlaceholder', '에버라인 상사')}
                  aria-label={t('users.memberTypeChange.businessName', '상호')}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="member-type-business-reg-no" required>
                  {t('users.memberTypeChange.businessRegistrationNo', '사업자등록번호')}
                </Label>
                <Input
                  id="member-type-business-reg-no"
                  value={businessRegistrationNo}
                  onChange={(e) => setBusinessRegistrationNo(e.target.value)}
                  maxLength={20}
                  disabled={mutation.isPending}
                  placeholder="000-00-00000"
                  aria-label={t('users.memberTypeChange.businessRegistrationNo', '사업자등록번호')}
                />
              </div>
            </div>
          ) : null}

          {error ? (
            <p
              role="alert"
              aria-live="polite"
              className="rounded-md p-2 text-sm"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
            >
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              {t('users.memberTypeChange.cancel', '취소')}
            </Button>
            <Button
              type="button"
              variant="cta"
              size="sm"
              disabled={mutation.isPending}
              onClick={handleConfirm}
            >
              {mutation.isPending
                ? t('users.memberTypeChange.submitting', '처리 중...')
                : t('users.memberTypeChange.confirm', '전환')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
